import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, EmptyState, Field } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, MessageSquare, Users } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : null);
const ageTone = (d) => (d >= 10 ? "text-red-700 bg-red-50 border-red-200" : d >= 5 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-zinc-600 bg-zinc-50 border-zinc-200");

/** Engagement requests a coordinator still owes the school a decision or a date on. */
export function EventRequests() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [declineFor, setDeclineFor] = useState(null);
  const [reason, setReason] = useState("");
  const [schedFor, setSchedFor] = useState(null);
  const [schedAt, setSchedAt] = useState("");
  const load = () => api.get("/my-event-requests").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const act = async (id, fn, msg) => {
    setBusy(id);
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const doSchedule = () => {
    if (!schedAt) return toast.error("Pick a date and time");
    const ev = schedFor;
    setSchedFor(null);
    act(ev.id, async () => {
      if (ev.status === "Requested") await api.post(`/engagement-events/${ev.id}/approve`);
      await api.post(`/engagement-events/${ev.id}/schedule`, { scheduled_at: new Date(schedAt).toISOString() });
    }, "Scheduled — invites and join links sent");
  };

  const awaiting = rows.filter((r) => r.status === "Requested").length;

  return (
    <>
      <Card title="Engagement requests" data-testid="event-requests-card"
        action={<div className="flex items-center gap-3">
          {awaiting > 0 && <span className="text-xs font-medium text-amber-700" data-testid="event-requests-awaiting">{awaiting} awaiting your decision</span>}
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => nav("/engagement")} data-testid="open-engagement-btn">Open Engagement</Button>
        </div>}>
        {rows.length === 0 ? <EmptyState text="No event requests waiting — you're all caught up." /> : (
          <div className="space-y-2">
            {rows.map((ev) => (
              <div key={ev.id} className="border rounded-md p-3" data-testid={`event-request-${ev.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900">{ev.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {ev.event_type}{ev.school_name ? ` · ${ev.school_name}` : ""}{ev.school_state ? ` (${ev.school_state})` : ""}
                      {ev.scheduled_at ? ` · requested for ${fmt(ev.scheduled_at)}` : " · no date requested"}
                    </p>
                    {ev.requester_line && <p className="text-xs text-zinc-500 mt-0.5">Requested by {ev.requester_line}</p>}
                    <p className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{ev.registration_count} signed up</span>
                      {ev.message_count > 0 && <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{ev.message_count} message{ev.message_count > 1 ? "s" : ""}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${ageTone(ev.waiting_days)}`} data-testid={`event-age-${ev.id}`}>
                      {ev.waiting_days === 0 ? "today" : `${ev.waiting_days}d waiting`}
                    </span>
                    <StatusBadge status={ev.status === "Draft" ? "Approved" : ev.status} />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {ev.status === "Requested" && (
                    <Button size="sm" disabled={busy === ev.id} className="h-7 text-xs bg-cc-green text-white"
                      onClick={() => act(ev.id, () => api.post(`/engagement-events/${ev.id}/approve`), "Approved — school notified")}
                      data-testid={`dash-approve-event-${ev.id}`}>Approve</Button>
                  )}
                  <Button size="sm" variant="outline" disabled={busy === ev.id} className="h-7 text-xs"
                    onClick={() => { setSchedFor(ev); setSchedAt(ev.scheduled_at ? new Date(ev.scheduled_at).toISOString().slice(0, 16) : ""); }}
                    data-testid={`dash-schedule-event-${ev.id}`}>
                    <CalendarClock className="w-3 h-3 mr-1" />{ev.status === "Requested" ? "Approve & schedule" : "Schedule"}
                  </Button>
                  {ev.status === "Requested" && (
                    <Button size="sm" variant="outline" disabled={busy === ev.id} className="h-7 text-xs text-red-600 border-red-200"
                      onClick={() => { setDeclineFor(ev); setReason(""); }} data-testid={`dash-decline-event-${ev.id}`}>Decline</Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-cc-blue"
                    onClick={() => nav(`/engagement?event=${ev.id}`)} data-testid={`dash-open-event-${ev.id}`}>Details & messages</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!declineFor} onOpenChange={(o) => !o && setDeclineFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Decline “{declineFor?.title}”</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">The school is emailed and notified in the app.</p>
          <Field label="Reason (optional)">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dash-decline-reason"
              placeholder="e.g. Timing conflicts with rotation schedule" />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineFor(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="dash-confirm-decline-btn"
              onClick={() => { const ev = declineFor; setDeclineFor(null); act(ev.id, () => api.post(`/engagement-events/${ev.id}/decline`, { reason }), "Declined — school notified"); }}>
              Decline &amp; notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!schedFor} onOpenChange={(o) => !o && setSchedFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{schedFor?.status === "Requested" ? "Approve & schedule" : "Schedule"} “{schedFor?.title}”</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">
            Creates the virtual room, sends the Outlook invite and emails the join link to all {schedFor?.registration_count || 0} registrant(s) and the school contact.
          </p>
          <Field label="Date & time">
            <Input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} data-testid="dash-sched-at" />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSchedFor(null)}>Cancel</Button>
            <Button className="bg-cc-blue text-white" onClick={doSchedule} data-testid="dash-confirm-schedule-btn">Schedule &amp; notify</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
